<template>
  <div class="font-mono text-xs leading-relaxed">
    <!-- Branch: object or array -->
    <template v-if="isBranch">
      <div class="flex items-start gap-1">
        <button
          type="button"
          class="shrink-0 mt-px w-4 text-surface-500 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-100 focus:outline-none focus:ring-1 focus:ring-rch-black rounded"
          :aria-expanded="isOpen"
          :aria-label="`${isOpen ? 'Collapse' : 'Expand'} ${label}`"
          :data-test-id="`json-viewer-toggle-${path}`"
          @click="isOpen = !isOpen"
        >
          <!--
            10px, not the 1rem primeicons defaults to: the caret sits against
            text-xs mono and reads as a marker beside the key, not as a control
            competing with it. The button keeps its w-4 box, so the hit target
            doesn't shrink with the glyph.
          -->
          <i
            :class="[
              isOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-right',
              'text-[10px]',
            ]"
          ></i>
        </button>
        <div class="min-w-0">
          <span
            v-if="nodeKey !== null"
            class="text-surface-600 dark:text-surface-300"
            >{{ nodeKey }}:
          </span>
          <span class="text-surface-500 dark:text-surface-400">
            {{ isArray ? '[' : '{' }}
            <span v-if="!isOpen">{{ sizeHint }}</span>
            <span v-if="!isOpen">{{ isArray ? ']' : '}' }}</span>
          </span>
          <span
            v-if="isOpen"
            class="ml-1 text-surface-400 dark:text-surface-500"
            >{{ sizeHint }}</span
          >
        </div>
      </div>

      <div
        v-if="isOpen"
        class="ml-4 border-l border-surface-200 dark:border-surface-700 pl-3"
        :data-test-id="`json-viewer-children-${path}`"
      >
        <!--
          Depth cap: partner payloads are arbitrary JSON, and a cyclic or
          pathologically nested one would otherwise recurse until the stack
          gives out. 20 levels is far past anything a real submission carries.
        -->
        <p
          v-if="depth >= maxDepth"
          class="text-surface-500 dark:text-surface-400 italic"
        >
          Maximum depth reached. Switch to Raw to see the rest.
        </p>
        <template v-else>
          <JsonTreeNode
            v-for="child in children"
            :key="child.path"
            :node-key="child.key"
            :value="child.value"
            :path="child.path"
            :depth="depth + 1"
            :max-depth="maxDepth"
            :initial-open-depth="initialOpenDepth"
            :expand-signal="expandSignal"
            :expand-target="expandTarget"
          />
        </template>
      </div>
      <div
        v-if="isOpen"
        class="ml-4 pl-3 text-surface-500 dark:text-surface-400"
      >
        {{ isArray ? ']' : '}' }}
      </div>
    </template>

    <!-- Leaf: primitive -->
    <div
      v-else
      class="flex items-start gap-1"
      :data-test-id="`json-viewer-leaf-${path}`"
    >
      <span class="shrink-0 w-4"></span>
      <div class="min-w-0 break-all">
        <span
          v-if="nodeKey !== null"
          class="text-surface-600 dark:text-surface-300"
          >{{ nodeKey }}:
        </span>
        <span :class="primitiveClass">{{ primitiveText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

interface Props {
  /** Property name or array index label. Null for the root node. */
  nodeKey: string | null
  value: unknown
  /** Dotted path from the root, used for stable keys and test ids. */
  path: string
  depth: number
  maxDepth: number
  initialOpenDepth: number
  /** Bumped by the viewer's Expand all / Collapse all buttons. */
  expandSignal: number
  expandTarget: boolean
}

const props = defineProps<Props>()

const isArray = computed(() => Array.isArray(props.value))
const isBranch = computed(
  () => props.value !== null && typeof props.value === 'object',
)

const children = computed<Array<{ key: string; value: unknown; path: string }>>(
  () => {
    if (!isBranch.value) return []
    if (isArray.value) {
      return (props.value as unknown[]).map((item, index) => ({
        key: String(index),
        value: item,
        path: `${props.path}.${index}`,
      }))
    }
    return Object.entries(props.value as Record<string, unknown>).map(
      ([key, value]) => ({ key, value, path: `${props.path}.${key}` }),
    )
  },
)

const sizeHint = computed(() => {
  const count = children.value.length
  if (isArray.value) return count === 1 ? '1 item' : `${count} items`
  return count === 1 ? '1 key' : `${count} keys`
})

const label = computed(() => props.nodeKey ?? 'root')

const isOpen = ref(props.depth < props.initialOpenDepth)

// Expand all / Collapse all broadcast through a counter rather than a boolean
// so a second click of the same button still reaches nodes the operator
// toggled by hand in between.
watch(
  () => props.expandSignal,
  () => {
    isOpen.value = props.expandTarget
  },
)

const primitiveText = computed(() => {
  const value = props.value
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value === 'string') return `"${value}"`
  return String(value)
})

/*
 * Values are separated from keys by contrast, not by hue.
 *
 * This used to colour string / number / boolean in three different hues, none
 * of which the rest of the app uses anywhere — and the distinction was already
 * carried by the quotes `primitiveText` adds, so the colour said nothing the
 * text didn't. What an operator scanning a payload actually needs is value
 * louder than key, and key louder than punctuation, which the surface scale
 * gives on its own.
 */
const primitiveClass = computed(() => {
  const value = props.value
  if (value === null || value === undefined) {
    return 'text-surface-500 dark:text-surface-400 italic'
  }
  return 'text-surface-900 dark:text-surface-0'
})
</script>
